import { coordEq, coordFromKey, coordKey, getTile, neighbors, unitAt } from "./grid";
import { canTraverse, terrainEnterCost } from "./pathfinding-travel";
import type { Coord, GameState, Unit } from "./types";
import { UNIT_DEFS } from "./units";

export { terrainDistancesFrom } from "./terrain-distance";

function relaxBfsNeighbor({
  state,
  unit,
  best,
  stack,
  remaining,
  n,
}: {
  state: GameState;
  unit: Unit;
  best: Map<string, number>;
  stack: Array<{ pos: Coord; remaining: number }>;
  remaining: number;
  n: Coord;
}): void {
  if (!canTraverse({ state, unit, coord: n })) return;
  const tile = getTile(state, n);
  const step = terrainEnterCost(tile, unit.kind);
  if (step === null) return;
  const newRemaining = remaining - step;
  if (newRemaining < 0) return;
  const key = coordKey(n);
  const prev = best.get(key);
  if (prev !== undefined && prev >= newRemaining) return;
  best.set(key, newRemaining);
  stack.push({ pos: n, remaining: newRemaining });
}

function bfsBestDistances(state: GameState, unit: Unit): Map<string, number> {
  const def = UNIT_DEFS[unit.kind];
  const best = new Map<string, number>();
  best.set(coordKey(unit.pos), def.movement);

  const stack: Array<{ pos: Coord; remaining: number }> = [
    { pos: unit.pos, remaining: def.movement },
  ];
  while (stack.length > 0) {
    const { pos, remaining } = stack.pop() as {
      pos: Coord;
      remaining: number;
    };
    for (const n of neighbors(pos)) {
      relaxBfsNeighbor({ state, unit, best, stack, remaining, n });
    }
  }

  return best;
}

function reachableCoordsFromBest({
  state,
  unit,
  best,
}: {
  state: GameState;
  unit: Unit;
  best: Map<string, number>;
}): Coord[] {
  const result: Coord[] = [];
  for (const key of best.keys()) {
    const c = coordFromKey(key);
    if (coordEq(c, unit.pos)) {
      result.push(c);
      continue;
    }
    if (!unitAt(state, c)) {
      result.push(c);
    }
  }
  return result;
}

export function bfsMovementRange(state: GameState, unit: Unit): Coord[] {
  const best = bfsBestDistances(state, unit);
  return [...best.keys()].map(coordFromKey);
}

export function bfsReachable(state: GameState, unit: Unit): Coord[] {
  const best = bfsBestDistances(state, unit);
  return reachableCoordsFromBest({ state, unit, best });
}

function relaxShortestPathNeighbor({
  state,
  unit,
  queue,
  cost,
  prev,
  movementBudget,
  from,
  fromCost,
  n,
}: {
  state: GameState;
  unit: Unit;
  queue: Array<{ pos: Coord; c: number }>;
  cost: Map<string, number>;
  prev: Map<string, Coord>;
  movementBudget: number;
  from: Coord;
  fromCost: number;
  n: Coord;
}): void {
  if (!canTraverse({ state, unit, coord: n })) return;
  const tile = getTile(state, n);
  const step = terrainEnterCost(tile, unit.kind);
  if (step === null) return;
  const newCost = fromCost + step;
  if (newCost > movementBudget) return;
  const key = coordKey(n);
  const prevCost = cost.get(key);
  if (prevCost !== undefined && prevCost <= newCost) return;
  cost.set(key, newCost);
  prev.set(key, from);
  queue.push({ pos: n, c: newCost });
}

function shortestPathSearch({
  state,
  unit,
  movementBudget,
}: {
  state: GameState;
  unit: Unit;
  movementBudget: number;
}): { cost: Map<string, number>; prev: Map<string, Coord> } {
  const cost = new Map<string, number>();
  const prev = new Map<string, Coord>();
  cost.set(coordKey(unit.pos), 0);

  const queue: Array<{ pos: Coord; c: number }> = [{ pos: unit.pos, c: 0 }];
  while (queue.length > 0) {
    queue.sort((a, b) => a.c - b.c);
    const { pos, c } = queue.shift() as { pos: Coord; c: number };
    const known = cost.get(coordKey(pos));
    if (known === undefined || c > known) continue;
    for (const n of neighbors(pos)) {
      relaxShortestPathNeighbor({
        state,
        unit,
        queue,
        cost,
        prev,
        movementBudget,
        from: pos,
        fromCost: c,
        n,
      });
    }
  }
  return { cost, prev };
}

function reconstructShortestPath({
  prev,
  start,
  dest,
}: {
  prev: Map<string, Coord>;
  start: Coord;
  dest: Coord;
}): Coord[] | null {
  const path: Coord[] = [];
  let cur: Coord = dest;
  while (!coordEq(cur, start)) {
    path.unshift(cur);
    const p = prev.get(coordKey(cur));
    if (!p) return null;
    cur = p;
  }
  return path;
}

export function shortestPath({
  state,
  unit,
  dest,
  options,
}: {
  state: GameState;
  unit: Unit;
  dest: Coord;
  options?: { allowOccupiedDestination?: boolean };
}): Coord[] | null {
  const def = UNIT_DEFS[unit.kind];
  if (coordEq(unit.pos, dest)) return [];

  const { cost, prev } = shortestPathSearch({ state, unit, movementBudget: def.movement });

  if (!cost.has(coordKey(dest))) return null;
  const occupant = unitAt(state, dest);
  if (occupant && !coordEq(dest, unit.pos) && !options?.allowOccupiedDestination) return null;

  return reconstructShortestPath({ prev, start: unit.pos, dest });
}
